import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDataGrid, type SystemGridColDef } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { DIFF_REVIEW } from "../../../../../theme/tokens";
import { AcceptCell, FieldCell, NotesCell, ValuesCell } from "./AirtableDiffCells";
import { AirtableSourceEvidence, hasSourceEvidence } from "./AirtableSourceEvidence";
import { AIRTABLE_EVIDENCE_HEIGHT } from "./consts";
import type { AirtableReviewEntry } from "./useAirtableReview";

type Props = {
	entries: AirtableReviewEntry[];
	onCheckedChange: (rowId: string, checked: boolean) => void;
	/** Freezes every control while the apply is in flight. */
	disabled: boolean;
};

/** What a grid row is, flattened so the grid's generics have concrete fields to key on. */
type DiffGridRow = AirtableReviewEntry & { id: string };

/**
 * The per-field review table: one row per config field, with what the department holds now beside what
 * the Airtable record would write, and a checkbox per row. Cell bodies live in `AirtableDiffCells`.
 *
 * ── There is deliberately NO "accept all" and NO select-all header checkbox. ──────────────────────────
 * This is the anti-rubber-stamping measure the flow is built around, not an oversight and not a missing
 * convenience. Roughly half these rows are values a language model pulled out of free-text intake notes;
 * accepting one is a human asserting they read the source passage and agree it says this. A single
 * control that flips forty of those to accepted turns that assertion into a formality, and the failure
 * it produces — a wrong bed count or a rolled-back rule set, applied because somebody clicked once —
 * is silent and lands in a live department's config.
 *
 * "Add a select-all" is the single most likely review suggestion on this file. The answer is no: the
 * cost of the flow being tedious is the point of it. `useAirtableReview` mirrors this — it exposes no
 * bulk setter for a caller to reach for either. Note that `SystemDataGrid`'s `checkboxSelection` is
 * therefore unused here: it would bring MUI's header select-all with it.
 */
export const AirtableDiffGrid: FC<Props> = ({ entries, onCheckedChange, disabled }) => {
	const { t } = useTranslation();

	// `rowId`, never `fieldKey` — one destination field can produce several rows, and a shared grid id
	// makes MUI treat them as one row, silently sharing accept and expansion state. See `rowIdFor`.
	const rows: DiffGridRow[] = useMemo(() => entries.map((entry) => ({ ...entry, id: entry.rowId })), [entries]);

	// Memoized so a checkbox click — which only changes `entries` — does not hand MUI a fresh `columns`
	// identity and make it re-evaluate every row's layout.
	const columns: SystemGridColDef<DiffGridRow>[] = useMemo(
		() => [
			{
				field: "accept",
				headerName: t("department.config.airtable.column.accept"),
				width: 92,
				sortable: false,
				disableColumnMenu: true,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => <AcceptCell entry={row} disabled={disabled} onCheckedChange={onCheckedChange} />,
			},
			{
				field: "field",
				headerName: t("department.config.airtable.column.field"),
				width: 190,
				sortable: false,
				disableColumnMenu: true,
				renderCell: ({ row }) => <FieldCell entry={row} />,
			},
			{
				field: "values",
				headerName: t("department.config.airtable.column.values"),
				flex: 1,
				minWidth: 320,
				sortable: false,
				disableColumnMenu: true,
				renderCell: ({ row }) => <ValuesCell entry={row} />,
			},
			{
				// Refusals only: the server's reason for a row nobody can accept, plus the plausible range an
				// out-of-band value missed. An ordinary row leaves it empty, which is the point — the column
				// is where the eye goes to find out why a row has no checkbox.
				field: "notes",
				headerName: t("department.config.airtable.column.notes"),
				width: 230,
				sortable: false,
				disableColumnMenu: true,
				renderCell: ({ row }) => <NotesCell entry={row} />,
			},
		],
		[t, disabled, onCheckedChange],
	);

	return (
		<SystemDataGrid<DiffGridRow>
			rows={rows}
			columns={columns}
			getRowId={(row) => row.id}
			containedHeight={{ minHeight: DIFF_REVIEW.ROW_MIN_HEIGHT * 3, maxHeight: DIFF_REVIEW.GRID_MAX_HEIGHT }}
			// Gated on EVIDENCE, not on `kind`. A row whose cell had prose the model read and found nothing
			// in has a passage worth expanding even though it is not an `llm` row — gating on
			// `kind === "llm"` hid exactly that. A deterministic copy still has nothing to show beyond its
			// two values.
			getRowExpandContent={(row) => (hasSourceEvidence(row.row) ? <AirtableSourceEvidence row={row.row} rowId={row.rowId} /> : null)}
			getExpandContentHeight={() => AIRTABLE_EVIDENCE_HEIGHT}
			// A DataGrid cell is `white-space: nowrap; overflow: hidden` by default, which silently CLIPS a
			// rule line mid-word — and the clipped tail is often the member that changed. Targeted at the
			// MUI class rather than via a bare `sx`, which ties on specificity and loses on source order.
			sx={{
				"& .MuiDataGrid-cell": { whiteSpace: "normal", alignItems: "flex-start" },
			}}
			dataGridProps={{
				// Row heights are MEASURED, not computed: a value line wraps to however many line boxes the
				// column width gives it, so any arithmetic from the line count is wrong at some width and
				// clips exactly the removals this grid exists to show. `auto` costs a ResizeObserver per row,
				// which is the reason `SystemDataGrid` refuses it for its own settled detail rows — the trade
				// is fine here because a config diff is tens of rows inside a modal, not a paged data table.
				getRowHeight: () => "auto",
			}}
			dataTestId="airtable-diff-grid"
		/>
	);
};
